import { useTranslation } from 'react-i18next'

import { Avatar } from '@/components/ui/Avatar'
import { useFormat } from '@/i18n/useFormat'
import { cn } from '@/lib/cn'
import { placeName } from '@/lib/countries'
import { formatPercent, splitMoney } from '@/lib/format'
import { modeLabelKey } from '@/lib/icons'
import type { QuoteSummary } from '@/types/domain'

/* The board form of a quote.
 *
 * Its own component rather than a RecordCard call, because a quote on the board
 * answers a different question from a row in a list. The list answers "which
 * quote is this"; the board answers "can this one be moved on today" — and that
 * needs the money, the clock and whatever is BLOCKING it, three things the
 * generic two-field card has nowhere to put.
 *
 * Reading order is fixed and top-down, because a lane is scanned vertically and
 * every card must put the same thing in the same place:
 *
 *   number + revision      identity, and which version of it went out
 *   client                 who it is for
 *   route · cargo · mode   what was actually quoted
 *   cargo description      what it is, in the words the quote was taken in
 *   ── figures ──          total, margin, valid-to, on a rule of their own
 *   owner + flag           who holds it, and what is wrong with it
 *
 * The figures sit under a hairline in a right-aligned column: they are the
 * comparable part, and comparison across cards only works if the digits share
 * an edge. Everything above the rule is prose about ONE quote; everything below
 * it is a number that means something against the card above and below.
 *
 * Status is absent by design — the lane already says it (RecordBoard's
 * renderCard contract). On the phone, where lanes become headed groups, the
 * heading still carries it.
 */

export interface QuoteCardProps {
  quote: QuoteSummary
  /** What is holding the quote up — a checklist blocker, or client silence.
   *  The board's whole purpose is surfacing these, so it is passed in rather
   *  than derived here: only the page knows which of them applies today. */
  flag?: { label: string; tone: 'attention' | 'muted' }
}

export function QuoteCard({ quote, flag }: QuoteCardProps) {
  const { t, i18n } = useTranslation()
  const format = useFormat()

  const total = splitMoney(quote.sell?.amount, quote.sell?.currency ?? 'USD')

  /* The route line: the lane, what is on it, and how it goes. Each part is
     dropped rather than defaulted when absent — a header-only quote (FR-3) has
     no lane yet, and an em dash between two blanks is worse than no line.

     "40'HC × 2" only when the whole quote shares one package type; a mixed
     quote counts positions instead, because naming one type there would
     describe a fraction of what is being shipped (the backend decides this and
     sends packageType empty — the card does not re-litigate it). */
  const lane =
    quote.origin && quote.destination
      ? `${placeName(quote.origin, i18n.language)} → ${placeName(quote.destination, i18n.language)}`
      : undefined

  const cargo = quote.cargo
  const cargoLabel = !cargo?.positions
    ? undefined
    : cargo.packageType
      ? `${cargo.packageType} × ${cargo.packages}`
      : t('quotes.card.positions', { count: cargo.positions })

  const detail = [
    lane,
    cargoLabel,
    quote.mode ? t(modeLabelKey(quote.mode)) : undefined,
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <div className="flex flex-col gap-2.5">
      {/* Identity. The number is the handle people say out loud, so it leads
          and carries the brand weight; the revision and date trail in muted
          text because they qualify the number rather than competing with it.
          "Rev A · 26 Jul" — the revision only once one has issued: citing a
          version on a never-sent draft would claim an offer is out. */}
      <div className="flex items-baseline gap-2">
        {/* The number never wraps and never truncates: it is the handle people
            say out loud, and half of it is not a handle. When the lane is too
            narrow for both, the line that gives way is the qualifier beside it. */}
        <span className="text-brand shrink-0 text-[13px] font-semibold whitespace-nowrap">
          {quote.number}
        </span>
        <span className="flex-1" />
        {quote.revisionLabel || quote.quoteDate ? (
          <span className="text-text-faint min-w-0 truncate text-[11px] font-light">
            {[
              quote.revisionLabel
                ? t('quotes.card.rev', { label: quote.revisionLabel })
                : undefined,
              quote.quoteDate ? format.date(quote.quoteDate) : undefined,
            ]
              .filter(Boolean)
              .join(' · ')}
          </span>
        ) : null}
      </div>

      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="text-text-heading truncate text-[13.5px]">
          {quote.clientName}
        </span>
        {detail ? (
          <span className="text-text-muted truncate text-[12px] font-light">
            {detail}
          </span>
        ) : null}
        {/* What is being shipped, in the salesperson's own words — the quote
            header's one-line description, typed at creation. Its own line
            rather than another dot-separated part of the route line above:
            that line is a set of short facts scanned sideways, and a free-text
            sentence appended to it pushes the mode off the end of the card.
            Faint and truncated, because it qualifies the row rather than
            competing with the client's name; the full text is on hover.

            It reads from the header field on purpose, and the offer PDF
            deliberately does NOT print it — there the Cargo tab's declared
            positions are the only account of the consignment. This is working
            memory for the team, not a claim made to a client. */}
        {quote.cargoDescription ? (
          <span
            className="text-text-faint truncate text-[12px] font-light"
            title={quote.cargoDescription}
          >
            {quote.cargoDescription}
          </span>
        ) : null}
      </div>

      {/* The figures. A hairline above rather than a filled block: the card is
          already a surface, and a second surface inside it would read as a
          nested card. Labels left, values right — the label column is read once
          and then ignored, the value column is what the eye runs down. */}
      <dl className="border-line-hair flex flex-col gap-1 border-t pt-2.5">
        <Figure label={t('quotes.card.total')}>
          <span className="asa-num text-text-heading text-[13px] font-medium">
            {total.amount}
          </span>
          <span className="text-text-faint ml-1 text-[11px]">{total.currency}</span>
        </Figure>

        {quote.marginPercent ? (
          <Figure label={t('quotes.card.margin')}>
            <span className="asa-num text-text-body text-[12.5px]">
              {formatPercent(quote.marginPercent)}
            </span>
          </Figure>
        ) : null}

        {quote.validUntil ? (
          <Figure label={t('quotes.card.validTo')}>
            <span className="asa-num text-text-body text-[12.5px]">
              {format.date(quote.validUntil)}
            </span>
          </Figure>
        ) : null}
      </dl>

      {/* Owner and flag share the last line: "who has this" and "what is wrong
          with it" are the two things asked at standup, and they belong to each
          other. The line is dropped entirely when a quote has neither. */}
      {quote.ownerName || flag ? (
        <div className="flex items-center gap-2 pt-0.5">
          {quote.ownerName ? (
            <span className="flex min-w-0 items-center gap-1.5">
              <Avatar name={quote.ownerName} size="sm" />
              <span className="text-text-muted truncate text-[12px] font-light">
                {quote.ownerName}
              </span>
            </span>
          ) : null}
          <span className="flex-1" />
          {flag ? (
            <span
              className={cn(
                'shrink-0 truncate text-[11px]',
                flag.tone === 'attention'
                  ? 'bg-attention-soft text-attention rounded-[var(--radius-sm)] px-1.5 py-0.5 font-medium'
                  : 'text-text-faint font-light',
              )}
            >
              {flag.label}
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

/** One label/value row of the figures block. */
function Figure({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-text-muted text-[11.5px] font-light">{label}</dt>
      <dd className="shrink-0 text-right">{children}</dd>
    </div>
  )
}
